import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { coursesSeed } from "../data/mockData";
import { adminUserCreateAction, adminUserGetAction, adminUserUpdateAction } from "../Action/action";
import config from "../coreFIles/config";
import { toast } from "react-toastify";

export default function UserForm({ mode }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loadingUser, setLoadingUser] = useState(mode === "edit");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("Active");

  const [relationshipToChild, setRelationshipToChild] = useState("Parent");
  const [adultAge, setAdultAge] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [timeMorning, setTimeMorning] = useState("08:00");
  const [timeAfternoon, setTimeAfternoon] = useState("14:00");
  const [timeEvening, setTimeEvening] = useState("20:00");
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
  const [existingAvatarUrl, setExistingAvatarUrl] = useState("");
  const [avatarInputKey] = useState(0);

  const [children, setChildren] = useState([
    { id: "child-1", name: "", age: "", gender: "", birthOrder: "", siblings: "" }
  ]);
  const [childErrors, setChildErrors] = useState({});

  const [assigned, setAssigned] = useState([]);
  const [assignCourseId, setAssignCourseId] = useState("");
  const [assignChildId, setAssignChildId] = useState("child-1");
  const [assignFree, setAssignFree] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const title = mode === "edit" ? "Edit User" : "Add New User";
  const showAssignCourses = false;

  const resolvePublicUrl = (pathOrUrl) => {
    if (!pathOrUrl) return "";
    const s = String(pathOrUrl);
    if (/^https?:\/\//i.test(s)) return s;
    // uploads are served by backend at /uploads (not under /api/v1)
    let origin = "";
    try {
      if (config.apiUrl) origin = new URL(config.apiUrl).origin;
    } catch (e) {
      origin = "";
    }
    if (!origin) origin = "http://localhost:3001";
    if (s.startsWith("/")) return `${origin}${s}`;
    return `${origin}/${s}`;
  };

  const morningOptions = useMemo(
    () => [
      { label: "6 AM", value: "06:00" },
      { label: "8 AM", value: "08:00" },
      { label: "10 AM", value: "10:00" }
    ],
    []
  );
  const afternoonOptions = useMemo(
    () => [
      { label: "12 PM", value: "12:00" },
      { label: "2 PM", value: "14:00" },
      { label: "4 PM", value: "16:00" }
    ],
    []
  );
  const eveningOptions = useMemo(
    () => [
      { label: "6 PM", value: "18:00" },
      { label: "8 PM", value: "20:00" },
      { label: "9 PM", value: "21:00" }
    ],
    []
  );

  useEffect(() => {
    if (!avatarFile) {
      if (avatarPreviewUrl) setAvatarPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarFile]);

  const splitName = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { first: "", last: "" };
    if (parts.length === 1) return { first: parts[0], last: "" };
    return { first: parts[0], last: parts.slice(1).join(" ") };
  };

  useEffect(() => {
    let cancelled = false;
    async function loadUser() {
      if (mode !== "edit") return;
      setLoadingUser(true);
      setError("");
      try {
        const res = await adminUserGetAction(userId);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load user.");
          return;
        }
        const u = res.data || {};
        const nm = splitName(u.name || "");
        setFirstName(nm.first);
        setLastName(nm.last);
        setEmail(u.email || "");
        setPhone(String(u.phone || "").replace(/\D+/g, "").slice(0, 10));
        setStatus(u.is_active !== undefined && u.is_active !== null ? (Number(u.is_active) ? "Active" : "Inactive") : "Active");

        setRelationshipToChild(u.relationship_to_child || "Parent");
        setAdultAge(u.adult_age !== undefined && u.adult_age !== null ? String(u.adult_age) : "");

        // notification time settings (HH:MM)
        setTimeMorning(u.time_morning || "08:00");
        setTimeAfternoon(u.time_afternoon || "14:00");
        setTimeEvening(u.time_evening || "20:00");
        setNotificationsEnabled(u.notifications_enabled !== undefined && u.notifications_enabled !== null ? !!Number(u.notifications_enabled) : true);
        setExistingAvatarUrl(resolvePublicUrl(u.avatar_url));

        // children
        if (Array.isArray(u.children) && u.children.length) {
          const usedOrders = new Set(
            u.children
              .map((c) => Number(c?.order_of_birth))
              .filter((n) => Number.isFinite(n) && n > 0)
          );
          let nextOrder = 1;
          const allocNextOrder = () => {
            while (usedOrders.has(nextOrder)) nextOrder += 1;
            usedOrders.add(nextOrder);
            return nextOrder;
          };

          const mappedChildren = u.children.map((c, idx) => {
            const existingOrder =
              c?.order_of_birth !== undefined && c?.order_of_birth !== null && String(c.order_of_birth).trim() !== ""
                ? Number(c.order_of_birth)
                : null;
            return {
              id: `child-${c.id ?? idx + 1}`,
              name: c.name || "",
              age: c.age !== undefined && c.age !== null ? String(c.age) : "",
              gender: c.gender || "",
              // If DB has NULL order_of_birth for existing records, auto-fill sequentially so edit screen doesn't show empty.
              birthOrder: existingOrder ? String(existingOrder) : String(allocNextOrder()),
              siblings: c.siblings !== undefined && c.siblings !== null ? String(c.siblings) : ""
            };
          });
          setChildren(mappedChildren);
          setAssignChildId(mappedChildren[0]?.id || "child-1");
        } else {
          setChildren([{ id: "child-1", name: "", age: "", gender: "", birthOrder: "", siblings: "" }]);
          setAssignChildId("child-1");
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load user.");
      } finally {
        if (!cancelled) setLoadingUser(false);
      }
    }
    loadUser();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, userId]);

  const addChild = () => {
    setChildren((prev) => [
      ...prev,
      { id: `child-${prev.length + 1}`, name: "", age: "", gender: "", birthOrder: "", siblings: "" }
    ]);
  };

  const removeChild = (id) => {
    setChildren((prev) => prev.filter((c) => c.id !== id));
    setAssigned((prev) => prev.filter((a) => a.childId !== id));
    setChildErrors((prev) => {
      if (!prev || !prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const updateChild = (id, patch) => {
    setChildren((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    Object.keys(patch || {}).forEach((k) => clearChildFieldError(id, k));
  };

  const addAssignment = () => {
    if (!assignCourseId || !assignChildId) return;
    const course = coursesSeed.find((c) => c.id === assignCourseId);
    if (!course) return;
    setAssigned((prev) => [
      ...prev,
      {
        id: `as-${Date.now()}`,
        courseId: course.id,
        courseName: course.name,
        childId: assignChildId,
        free: assignFree
      }
    ]);
    setAssignCourseId("");
    setAssignFree(false);
  };

  const removeAssignment = (id) => setAssigned((prev) => prev.filter((a) => a.id !== id));

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev || !prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearChildFieldError = (childId, key) => {
    setChildErrors((prev) => {
      if (!prev || !prev[childId] || !prev[childId][key]) return prev;
      const next = { ...prev };
      next[childId] = { ...next[childId] };
      delete next[childId][key];
      if (Object.keys(next[childId]).length === 0) delete next[childId];
      return next;
    });
  };

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  const renderFieldError = (msg) => {
    if (!msg) return null;
    const s = String(msg);
    const idx = s.toLowerCase().indexOf("is required");
    if (idx >= 0) {
      const before = s.slice(0, idx);
      const after = s.slice(idx + "is required".length);
      return (
        <>
          <span>{before}</span>
          <span className="text-danger">is required</span>
          <span>{after}</span>
        </>
      );
    }
    return s;
  };

  const validateForm = () => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = "First Name is required.";
    if (!lastName.trim()) errs.lastName = "Last Name is required.";
    if (!email.trim()) errs.email = "Email is required.";
    else if (!isValidEmail(email)) errs.email = "Please enter a valid email.";

    if (!relationshipToChild || !String(relationshipToChild).trim()) {
      errs.relationshipToChild = "Relationship to child is required.";
    }
    if (adultAge === "" || adultAge === null || adultAge === undefined) {
      errs.adultAge = "Age is required.";
    } else {
      const n = Number(adultAge);
      if (Number.isNaN(n) || n <= 0 || n > 120) {
        errs.adultAge = "Please enter a valid age.";
      }
    }

    const phoneDigits = String(phone || "").replace(/\D+/g, "");
    if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
      errs.phone = "Phone Number must be exactly 10 digits.";
    }

    // Children: if any field is started for a child, then all fields are required for that child.
    const nextChildErrors = {};
    const usedBirthOrders = new Map(); // number -> childId
    children.forEach((c) => {
      const anyStarted =
        !!String(c?.name || "").trim() ||
        String(c?.age || "").trim() !== "" ||
        String(c?.gender || "").trim() !== "" ||
        String(c?.birthOrder || "").trim() !== "" ||
        String(c?.siblings || "").trim() !== "";

      if (!anyStarted) return;

      const ce = {};

      if (!String(c?.name || "").trim()) ce.name = "Child Name is required.";

      const ageVal = String(c?.age || "").trim();
      if (!ageVal) ce.age = "Age is required.";
      else {
        const n = Number(ageVal);
        if (Number.isNaN(n) || n < 1 || n > 18) ce.age = "Please enter a valid age (1-18).";
      }

      const g = String(c?.gender || "").trim();
      if (!g) ce.gender = "Gender is required.";

      const boVal = String(c?.birthOrder || "").trim();
      if (!boVal) ce.birthOrder = "Birth order is required.";
      else {
        const n = Number(boVal);
        if (Number.isNaN(n) || n < 1 || n > 20) ce.birthOrder = "Please enter a valid birth order.";
        else {
          const existing = usedBirthOrders.get(n);
          if (existing && existing !== c.id) ce.birthOrder = "Birth order must be unique.";
          else usedBirthOrders.set(n, c.id);
        }
      }

      const sibVal = String(c?.siblings || "").trim();
      if (sibVal === "") ce.siblings = "Siblings is required.";
      else {
        const n = Number(sibVal);
        if (Number.isNaN(n) || n < 0) ce.siblings = "Please enter a valid number (0 or more).";
      }

      if (Object.keys(ce).length) nextChildErrors[c.id] = ce;
    });

    setChildErrors(nextChildErrors);
    if (Object.keys(nextChildErrors).length) {
      errs.children = "Please complete all required child fields.";
    }

    if (mode === "create") {
      if (!password || !password.trim()) errs.password = "Password is required.";
      else {
        const hasUpper = /[A-Z]/.test(password);
        const hasDigit = /\d/.test(password);
        const hasSpecial = /[^A-Za-z0-9]/.test(password);
        if (password.length < 8 || !hasUpper || !hasDigit || !hasSpecial) {
          errs.password =
            "Password must be at least 8 characters and include at least 1 uppercase letter, 1 number, and 1 special character.";
        }
      }
      if (!confirmPassword || !confirmPassword.trim()) errs.confirmPassword = "Confirm Password is required.";
      else if (password !== confirmPassword) errs.confirmPassword = "Passwords do not match.";
    }
    return errs;
  };

  const normalizeApiMessage = (m) => {
    if (!m) return "Something went wrong.";
    if (typeof m === "string") return m;
    if (typeof m === "object") {
      // backend sometimes returns { field: "error" }
      const vals = Object.values(m).filter(Boolean).map(String);
      if (vals.length) return vals.join("\n");
      try {
        return JSON.stringify(m);
      } catch (e) {
        return "Something went wrong.";
      }
    }
    return String(m);
  };

  const buildChildrenPayload = () => {
    const started = children
      .map((c) => {
        const anyStarted =
          !!String(c?.name || "").trim() ||
          String(c?.age || "").trim() !== "" ||
          String(c?.gender || "").trim() !== "" ||
          String(c?.birthOrder || "").trim() !== "" ||
          String(c?.siblings || "").trim() !== "";
        if (!anyStarted) return null;

        // DB children.gender: ENUM Boy | Girl | Prefer Not To Say
        const ALLOWED_GENDER = new Set(["Boy", "Girl", "Prefer Not To Say"]);
        const g = String(c.gender || "").trim();
        let gender = null;
        if (g) {
          if (ALLOWED_GENDER.has(g)) gender = g;
          else if (g === "Male") gender = "Boy";
          else if (g === "Female") gender = "Girl";
          else if (g === "Other") gender = "Prefer Not To Say";
        }

        return {
          name: String(c.name || "").trim() || null,
          age: c.age !== "" && c.age !== null && c.age !== undefined ? Number(c.age) : null,
          gender,
          order_of_birth:
            c.birthOrder !== "" && c.birthOrder !== null && c.birthOrder !== undefined
              ? Number(c.birthOrder)
              : null,
          siblings:
            c.siblings !== "" && c.siblings !== null && c.siblings !== undefined ? Number(c.siblings) : null
        };
      })
      .filter(Boolean);

    return started.sort((a, b) => {
      const ao = a.order_of_birth;
      const bo = b.order_of_birth;
      if (ao == null && bo == null) return 0;
      if (ao == null) return 1;
      if (bo == null) return -1;
      return ao - bo;
    });
  };

  const onSave = async () => {
    const errs = validateForm();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError("Please fix the required fields.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", `${firstName} ${lastName}`.trim());
      fd.append("email", email.trim());
      const phoneDigits = String(phone || "").replace(/\D+/g, "");
      if (phoneDigits) fd.append("phone", phoneDigits);
      fd.append("role", "user");
      fd.append("is_active", status === "Active" ? "1" : "0");
      fd.append("relationship_to_child", relationshipToChild);
      fd.append("adult_age", String(adultAge));
      if (mode === "create") {
        fd.append("password", password);
      }
      fd.append("time_morning", timeMorning);
      fd.append("time_afternoon", timeAfternoon);
      fd.append("time_evening", timeEvening);
      fd.append("notifications_enabled", notificationsEnabled ? "1" : "0");
      // assigned courses not available in current admin users table (backend); kept as wireframe UI

      const ch = buildChildrenPayload();
      if (ch.length) fd.append("children", JSON.stringify(ch));

      if (avatarFile) fd.append("avatar", avatarFile);

      const res =
        mode === "edit"
          ? await adminUserUpdateAction(userId, fd)
          : await adminUserCreateAction(fd);
      if (!res?.success) {
        throw new Error(normalizeApiMessage(res?.message || res?.msg));
      }

      toast.success(res?.message || (mode === "edit" ? "User updated" : "User created"));
      navigate("/users");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        (mode === "edit" ? "Failed to update user." : "Failed to create user.");
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {mode === "edit" && loadingUser ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not save user.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/users">Users</Link>
          <span>›</span>
          <span id="breadcrumb-title">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/users"
            className="btn btn-outline btn-sm"
          >
            ← Back
          </Link>
        </div>
      </div>

      <>
        <div className="grid-2">
          <div>
            <div className="card mb-6">
              <h2 className="section-title">Adult Information</h2>

                <div className="form-group">
                  <label>
                    Your Relationship To Child <span className="text-danger">*</span>
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
                    {["Parent", "Guardian", "Other"].map((opt) => (
                      <label key={opt} style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="relationship-to-child"
                          value={opt}
                          checked={relationshipToChild === opt}
                          onChange={(e) => {
                            setRelationshipToChild(e.target.value);
                            clearFieldError("relationshipToChild");
                          }}
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                  {fieldErrors.relationshipToChild ? (
                    <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                      {renderFieldError(fieldErrors.relationshipToChild)}
                    </div>
                  ) : null}
                </div>

                <div className="form-group">
                  <label>
                    Age <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter your age"
                    value={adultAge}
                    onChange={(e) => {
                      setAdultAge(e.target.value);
                      clearFieldError("adultAge");
                    }}
                    min="1"
                    max="120"
                    aria-invalid={!!fieldErrors.adultAge}
                    style={fieldErrors.adultAge ? { borderColor: "var(--danger)" } : undefined}
                  />
                  {fieldErrors.adultAge ? (
                    <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                      {renderFieldError(fieldErrors.adultAge)}
                    </div>
                  ) : null}
                </div>
              </div>

          <div className="card mb-6">
            <h2 className="section-title">Personal Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label>
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter first name..."
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearFieldError("firstName");
                  }}
                  aria-invalid={!!fieldErrors.firstName}
                  style={fieldErrors.firstName ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.firstName ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {renderFieldError(fieldErrors.firstName)}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter last name..."
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearFieldError("lastName");
                  }}
                  aria-invalid={!!fieldErrors.lastName}
                  style={fieldErrors.lastName ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.lastName ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {renderFieldError(fieldErrors.lastName)}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="form-group">
              <label>
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                placeholder="Enter email address..."
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearFieldError("email");
                }}
                aria-invalid={!!fieldErrors.email}
                style={fieldErrors.email ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.email ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {renderFieldError(fieldErrors.email)}
                </div>
              ) : null}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter 10 digit number"
                  value={phone}
                  inputMode="numeric"
                  pattern="\d{10}"
                  maxLength={10}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D+/g, "").slice(0, 10);
                    setPhone(digits);
                    clearFieldError("phone");
                  }}
                  aria-invalid={!!fieldErrors.phone}
                  style={fieldErrors.phone ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.phone ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {renderFieldError(fieldErrors.phone)}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>
          </div>

          <div className="card mb-6">
            <h2 className="section-title">Account Security</h2>
            <p className="help-text">Set the initial password for this user. They can change it later from the app.</p>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Password {mode === "create" ? <span className="text-danger">*</span> : null}
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Enter password..."
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearFieldError("password");
                    // confirm password may become valid after password change
                    clearFieldError("confirmPassword");
                  }}
                  
                  aria-invalid={!!fieldErrors.password}
                  style={fieldErrors.password ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.password ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {renderFieldError(fieldErrors.password)}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>
                  Confirm Password {mode === "create" ? <span className="text-danger">*</span> : null}
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Confirm password..."
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    clearFieldError("confirmPassword");
                  }}
                  
                  aria-invalid={!!fieldErrors.confirmPassword}
                  style={fieldErrors.confirmPassword ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.confirmPassword ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {renderFieldError(fieldErrors.confirmPassword)}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <h2 className="section-title">Notification Time Settings</h2>
            <p className="help-text">
              Set the user's preferred notification times. These control when reminders are sent via the app.
            </p>

            <div className="time-settings-grid">
              <div className="time-setting-card-edit">
                <div className="time-icon">🌅</div>
                <h4>Morning</h4>
                <select
                  className="form-control time-input"
                  value={timeMorning}
                  onChange={(e) => setTimeMorning(e.target.value)}
                >
                  {morningOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="small-text mt-2">Daily morning reminder</p>
              </div>
              <div className="time-setting-card-edit">
                <div className="time-icon">☀️</div>
                <h4>Afternoon</h4>
                <select
                  className="form-control time-input"
                  value={timeAfternoon}
                  onChange={(e) => setTimeAfternoon(e.target.value)}
                >
                  {afternoonOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="small-text mt-2">Midday check-in</p>
              </div>
              <div className="time-setting-card-edit">
                <div className="time-icon">🌙</div>
                <h4>Evening</h4>
                <select
                  className="form-control time-input"
                  value={timeEvening}
                  onChange={(e) => setTimeEvening(e.target.value)}
                >
                  {eveningOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="small-text mt-2">Bedtime reflection</p>
              </div>
            </div>

            <div className="flex gap-3 items-center mt-4">
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="notifications-enabled"
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                />
                <label htmlFor="notifications-enabled">Push notifications enabled</label>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="card mb-6">
            <h2 className="section-title">Profile Photo</h2>
            <div
              className="upload-area p-sm"
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
            >
              <div className="avatar-upload" style={{ overflow: "hidden" }}>
                {avatarPreviewUrl || existingAvatarUrl ? (
                  <img
                    src={avatarPreviewUrl || existingAvatarUrl}
                    alt="Avatar preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  "👤"
                )}
              </div>
              <p className="font-sm">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p className="small-label">PNG, JPG up to 2MB</p>
              {avatarFile ? (
                <p className="small-label" style={{ marginTop: 8 }}>
                  Selected: <strong>{avatarFile.name}</strong>
                </p>
              ) : null}
            </div>
            <input
              key={avatarInputKey}
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setAvatarFile(f);
              }}
            />
          </div>

          <div className="card mb-6">
            <div className="card-header">
              <h2>Children</h2>
              <button className="btn btn-outline btn-sm" type="button" onClick={addChild}>
                + Add Child
              </button>
            </div>
            <p className="help-text">Add children linked to this parent account.</p>

            {children.map((c, idx) => (
              <div className="content-box" key={c.id}>
                <div className="flex justify-between items-center mb-3">
                  <strong className="text-13">Child {idx + 1}</strong>
                  {children.length > 1 ? (
                    <button
                      type="button"
                      className="action-link text-danger font-sm"
                      onClick={() => removeChild(c.id)}
                      style={{ background: "transparent", border: "none", padding: 0 }}
                    >
                      Remove
                    </button>
                  ) : (
                    <span className="tiny-text"> </span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group mb-3">
                    <label>Child Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter child's name..."
                      value={c.name}
                      onChange={(e) => updateChild(c.id, { name: e.target.value })}
                      aria-invalid={!!childErrors?.[c.id]?.name}
                      style={childErrors?.[c.id]?.name ? { borderColor: "var(--danger)" } : undefined}
                    />
                    {childErrors?.[c.id]?.name ? (
                      <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                        {childErrors[c.id].name}
                      </div>
                    ) : null}
                  </div>
                  <div className="form-group mb-3">
                    <label>Age</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 6"
                      min="1"
                      max="18"
                      value={c.age}
                      onChange={(e) => updateChild(c.id, { age: e.target.value })}
                      aria-invalid={!!childErrors?.[c.id]?.age}
                      style={childErrors?.[c.id]?.age ? { borderColor: "var(--danger)" } : undefined}
                    />
                    {childErrors?.[c.id]?.age ? (
                      <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                        {childErrors[c.id].age}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="form-row-3">
                  <div className="form-group mb-0">
                    <label>Gender</label>
                    <select
                      className="form-control"
                      value={c.gender}
                      onChange={(e) => updateChild(c.id, { gender: e.target.value })}
                      aria-invalid={!!childErrors?.[c.id]?.gender}
                      style={childErrors?.[c.id]?.gender ? { borderColor: "var(--danger)" } : undefined}
                    >
                      <option value="">Select...</option>
                      <option value="Boy">Boy</option>
                      <option value="Girl">Girl</option>
                      <option value="Prefer Not To Say">Prefer Not To Say</option>
                    </select>
                    {childErrors?.[c.id]?.gender ? (
                      <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                        {childErrors[c.id].gender}
                      </div>
                    ) : null}
                  </div>
                  <div className="form-group mb-0">
                    <label>Birth Order</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 1"
                      min="1"
                      max="20"
                      value={c.birthOrder}
                      onChange={(e) => updateChild(c.id, { birthOrder: e.target.value })}
                      aria-invalid={!!childErrors?.[c.id]?.birthOrder}
                      style={childErrors?.[c.id]?.birthOrder ? { borderColor: "var(--danger)" } : undefined}
                    />
                    {childErrors?.[c.id]?.birthOrder ? (
                      <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                        {childErrors[c.id].birthOrder}
                      </div>
                    ) : null}
                  </div>
                  <div className="form-group mb-0">
                    <label>Siblings</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 1"
                      min="0"
                      value={c.siblings}
                      onChange={(e) => updateChild(c.id, { siblings: e.target.value })}
                      aria-invalid={!!childErrors?.[c.id]?.siblings}
                      style={childErrors?.[c.id]?.siblings ? { borderColor: "var(--danger)" } : undefined}
                    />
                    {childErrors?.[c.id]?.siblings ? (
                      <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                        {childErrors[c.id].siblings}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}

            <div className="dashed-placeholder">
              <p className="text-13">
                Click <strong>"+ Add Child"</strong> to add another child
              </p>
            </div>
          </div>

          {showAssignCourses ? (
            <div className="card">
              <div className="card-header">
                <h2>Assign Courses</h2>
              </div>
              <p className="help-text">Optionally assign courses to this user during creation.</p>

              <div className="form-group">
                <label>Select Course</label>
                <select
                  className="form-control"
                  value={assignCourseId}
                  onChange={(e) => setAssignCourseId(e.target.value)}
                >
                  <option value="">Choose a course to assign...</option>
                  {coursesSeed.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.ageGroup}) — R {c.priceZar}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Assign to Child</label>
                <select
                  className="form-control"
                  value={assignChildId}
                  onChange={(e) => setAssignChildId(e.target.value)}
                >
                  {children.map((c, idx) => (
                    <option key={c.id} value={c.id}>
                      {c.name?.trim() ? c.name : `Child ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="skip-payment"
                  checked={assignFree}
                  onChange={(e) => setAssignFree(e.target.checked)}
                />
                <label htmlFor="skip-payment">Grant free access (skip payment)</label>
              </div>

              <button className="btn btn-outline btn-sm mt-4" type="button" onClick={addAssignment}>
                + Assign Course
              </button>

              {assigned.map((a) => {
                const childIndex = children.findIndex((c) => c.id === a.childId);
                const childLabel =
                  childIndex >= 0 ? (children[childIndex].name?.trim() ? children[childIndex].name : `Child ${childIndex + 1}`) : "—";
                return (
                  <div className="assigned-course" key={a.id}>
                    <div>
                      <strong>{a.courseName}</strong>
                      <p>
                        Assigned to: {childLabel} · {a.free ? "Free access" : "Paid access"}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="action-link text-danger font-sm"
                      onClick={() => removeAssignment(a.id)}
                      style={{ background: "transparent", border: "none", padding: 0 }}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        </div>

        <div className="form-actions">
          <button className="btn btn-primary" type="button" onClick={() => onSave()} disabled={saving || loadingUser}>
          {saving ? (
            "Saving…"
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                <path d="M17 21v-8H7v8" />
                <path d="M7 3v5h8" />
              </svg>
              Save User
            </>
          )}
          </button>
          <Link to="/users" className="btn btn-outline">
            Cancel
          </Link>
        </div>
      </>
    </div>
  );
}

